import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import LazyLoadTable from "src/app/modules/component/lazy-load-table/LazyLoadTable";
import { getPatientTreatmentSheetHistory, getPatientTreatmentSheetHistoryDetail } from "src/app/modules/phan-he-noi-tru-dieu-tri/services/BenhAnServices";
import { CODE } from "src/app/modules/utils/Constant";
import { formatDateTime } from "src/app/modules/utils/FormatUtils";
import { usePhanHeKhamBenhContext } from "../../contexts/PhanHeTiepDonContext";
import ThongTinPhieuKhamBenh from "./ThongTinPhieuKhamBenh";

interface LichSuPhieuDieuTriProps {
    patientId?: string;
}

const LichSuPhieuKhamBenh = ({ patientId }: LichSuPhieuDieuTriProps) => {
    const { statusChangedEvent } = usePhanHeKhamBenhContext();
    const [treatmentSheets, setTreatmentSheets] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedSheet, setSelectedSheet] = useState<any>(null);
    const [selectedSheetDetail, setSelectedSheetDetail] = useState<any>(null);

    useEffect(() => {
        if (patientId) fetchTreatmentSheets();
    }, [patientId, statusChangedEvent]);

    const fetchTreatmentSheets = async () => {
        if (!patientId) return;
        try {
            setLoading(true);
            const FROM_CIS = true;
            const response = await getPatientTreatmentSheetHistory(patientId, FROM_CIS);
            console.log("Treatment sheets response:", response);
            const responseCode = (response?.data as any)?.code;
            if (responseCode === "OK" || responseCode === 200) {
                const sheets = (response.data.data || []).map((sheet: any, index: number) => ({
                    ...sheet,
                    uniqueId: sheet.id || sheet.encounterId || `sheet-${index}`
                }));
                console.log("Treatment sheets data:", sheets);
                setTreatmentSheets(sheets);
                handleRowClick(sheets?.length ? sheets[0] : null);
            }
        } catch (error) {
            console.error("Error fetching treatment sheets:", error);
            toast.error("Không thể tải lịch sử");
        } finally {
            setLoading(false);
        }
    };

    const getSheetType = (sheet: any) => {
        // Phân loại dựa vào source và treatmentType
        if (sheet.source === "CIS") {
            return "Khám bệnh";
        }

        // Phiếu điều trị: treatmentType 1-3 = Nội trú, 4-6 = Ngoại trú
        if (sheet.treatmentType) {
            const isInpatient = [1, 2, 3].includes(sheet.treatmentType);
            return isInpatient ? "ĐT Nội trú" : "ĐT Ngoại trú";
        }

        // Fallback cho data cũ chưa có treatmentType
        return sheet.source === "INPATIENT" ? "ĐT Nội trú" : "Khám bệnh";
    };

    const getSheetBadgeColor = (sheet: any) => {
        const type = getSheetType(sheet);
        if (type === "Khám bệnh") return "bg-success";
        if (type === "ĐT Ngoại trú") return "bg-info";
        return "bg-primary"; // ĐT Nội trú
    };

    const handleRowClick = async (sheet: any) => {
        try {
            console.log("handleRowClick - sheet:", sheet);
            setSelectedSheet(sheet);
            if (sheet?.orderId) {
                const { data } = await getPatientTreatmentSheetHistoryDetail(sheet?.orderId);
                if (CODE.SUCCESS === data.code) {
                    setSelectedSheetDetail(data.data);
                }
            } else {
                setSelectedSheetDetail(null);
            }
        } catch (error) {
            toast.error("Không thể xem phiếu");
        }
    };

    // Define columns for LazyLoadTable
    const columns = [
        {
            name: "STT",
            field: "stt",
            width: 50,
            render: (_: any, index: number) => index + 1,
            cellStyle: { textAlign: "center" },
        },
        {
            name: "Thời gian",
            field: "prescriptionDate",
            width: 150,
            render: (row: any) =>
                formatDateTime(row.prescriptionDate || row.encounterDatetime),
        },
        {
            name: "Loại",
            field: "source",
            width: 120,
            render: (row: any) => (
                <span className={`badge ${getSheetBadgeColor(row)}`}>
                    {getSheetType(row)}
                </span>
            ),
            cellStyle: { textAlign: "center" },
        },
        {
            name: "Mã BA",
            field: "medicalRecordCode",
            width: 120,
            cellStyle: { textAlign: "center" },
            render: (row: any) => row.medicalRecordCode || "-",
        },
    ];

    return (
        <div className="d-flex overflow-hidden spaces h-calc-vh-350 px-16 py-5 bg-white">
            <div className="row flex-grow-1 w-100" style={{ minHeight: 0 }}>
                <div className="col-md-5 pe-2" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
                    <div className="card flex-grow-1" style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
                        <div className="card-body p-0 flex-grow-1" style={{ minHeight: 0, overflow: "hidden" }}>
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" />
                                </div>
                            ) : (
                                <LazyLoadTable
                                    columns={columns}
                                    axiosReq={() => Promise.resolve({
                                        data: {
                                            code: 200,
                                            data: treatmentSheets
                                        },
                                        status: 200,
                                        statusText: 'OK',
                                        headers: {},
                                        config: {} as any
                                    })}
                                    searchData={{ patientId }}
                                    onRowClick={handleRowClick}
                                    rowSeclected={selectedSheet}
                                    rowKey="uniqueId"
                                    urlData="data.data"
                                    isToggle={true}
                                    dependencies={[treatmentSheets]}
                                />
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-md-7 ps-2" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
                    <div className="card flex-grow-1" style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
                        <div className="card-body p-0 flex-grow-1" style={{ minHeight: 0 }}>
                            {selectedSheetDetail && (
                                <ThongTinPhieuKhamBenh selectedSheetDetail={selectedSheetDetail} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LichSuPhieuKhamBenh;
